"use strict";
// Notifications: what has actually happened on this account, newest first.
//
// There is no notification store anywhere in this system, and this screen
// deliberately does not pretend otherwise. Every row is derived from a
// fact some existing endpoint already returns — a file delivered, an order
// placed, a sign-in on a device, a sign-in method connected — so nothing
// is written, nothing is broadcast, and nothing is invented.
//
// Two consequences follow honestly from that, rather than being papered
// over: there is no unread state (there is nowhere to record that a row
// was seen), and there are no store announcements (nothing publishes
// them). What is here is real; what is absent is absent because it does
// not exist.
(function () {
    /** How many rows to show. Beyond this it stops being a notification list. */
    var MAX_ROWS = 30;
    function t(hubEl, key, fallback) {
        return hubEl.dataset[key] || fallback;
    }
    var esc = function (value) {
        return window.FlixHubFormat.escapeHtml(value);
    };
    var BELL_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>';
    var DOWNLOAD_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
    var PACKAGE_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.3 7 12 12l8.7-5"/><path d="M12 22V12"/></svg>';
    var DEVICE_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2.5" width="16" height="19" rx="3"/><line x1="10" y1="18.5" x2="14" y2="18.5"/></svg>';
    var LINK_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/></svg>';
    /**
     * The tones already in the Hub, reused rather than invented: the same
     * greens and oranges the order badges and cart toasts use, so a
     * notification about a delivered file reads as the same kind of good
     * news as the toast that appeared when it was added to the cart.
     */
    var TONE = {
        delivered: {
            background: "color-mix(in srgb, var(--success) 13%, transparent)",
            border: "color-mix(in srgb, var(--success) 36%, transparent)",
            color: "color-mix(in srgb, var(--success) 78%, #ffffff)",
        },
        order: {
            background: "rgba(255,122,26,.13)",
            border: "rgba(255,122,26,.34)",
            color: "var(--accent)",
        },
        security: { background: "var(--surface2)", border: "var(--border)", color: "var(--dim)" },
    };
    var NEUTRAL_TONE = TONE["security"];
    function sectionLabelHtml(label) {
        return ('<div style="font-family:var(--mono);font-size:10px;letter-spacing:2px;color:var(--accent);margin:0 0 10px;text-transform:uppercase">' +
            esc(label) +
            "</div>");
    }
    function timeOf(iso) {
        if (!iso)
            return null;
        var ms = new Date(iso).getTime();
        return Number.isNaN(ms) ? null : ms;
    }
    /**
     * A notification's age is the thing the reader actually wants — "2 days
     * ago" answers "is this new?" in a way a calendar date does not. Beyond
     * a week the date is more useful again, and the theme's own date
     * formatter takes over.
     */
    function whenLabel(hubEl, at, locale) {
        var minutes = Math.floor((Date.now() - at) / 60000);
        if (minutes < 2)
            return t(hubEl, "i18nNotificationsJustNow", "Just now");
        if (minutes < 60) {
            return t(hubEl, "i18nNotificationsMinutesAgo", "%%count%% minutes ago").replace("%%count%%", String(minutes));
        }
        var hours = Math.floor(minutes / 60);
        if (hours < 24) {
            return t(hubEl, "i18nNotificationsHoursAgo", "%%count%% hours ago").replace("%%count%%", String(hours));
        }
        var days = Math.floor(hours / 24);
        if (days < 7) {
            return t(hubEl, "i18nNotificationsDaysAgo", "%%count%% days ago").replace("%%count%%", String(days));
        }
        return window.FlixHubFormat.date(new Date(at).toISOString(), locale);
    }
    function fill(template, value) {
        return template.replace("%%name%%", value);
    }
    /**
     * Everything the account's own screens already know, turned into events.
     *
     * Each source is an endpoint that exists and is already used elsewhere in
     * the Hub. A source that fails contributes nothing rather than failing
     * the screen: a Shopify outage should not hide the fact that somebody
     * just signed in on a device you do not recognise.
     */
    async function collect(hubEl, profile) {
        var entries = [];
        // `Promise.allSettled` is ES2020 and this theme compiles to ES2019, so
        // the same thing is spelled out: each source resolves to its data or
        // to null, and the three are gathered together rather than in series.
        var settled = function (promise) {
            return promise.then(function (value) {
                return value;
            }, function () {
                return null;
            });
        };
        var gathered = await Promise.all([
            settled(window.FlixAuthClient.listDownloads()),
            settled(window.FlixAuthClient.listOrders({ limit: 10 })),
            settled(window.FlixAuthClient.listSessions()),
        ]);
        var downloads = gathered[0];
        if (downloads) {
            for (var d = 0; d < downloads.downloads.length; d += 1) {
                var file = downloads.downloads[d];
                if (!file)
                    continue;
                var deliveredAt = timeOf(file.deliveredAt);
                if (deliveredAt === null)
                    continue;
                entries.push({
                    at: deliveredAt,
                    tone: "delivered",
                    icon: DOWNLOAD_ICON,
                    title: fill(t(hubEl, "i18nNotificationsFileReady", "%%name%% is ready to download"), file.title || file.orderName),
                    detail: file.orderName,
                    panel: "downloads",
                });
            }
        }
        var orders = gathered[1];
        if (orders) {
            for (var o = 0; o < orders.orders.length; o += 1) {
                var order = orders.orders[o];
                if (!order)
                    continue;
                var placedAt = timeOf(order.processedAt);
                if (placedAt === null)
                    continue;
                entries.push({
                    at: placedAt,
                    tone: "order",
                    icon: PACKAGE_ICON,
                    title: fill(t(hubEl, "i18nNotificationsOrderPlaced", "Order %%name%% placed"), order.name),
                    detail: order.primaryLineItemTitle || "",
                    panel: "orders",
                });
            }
        }
        var sessions = gathered[2];
        if (sessions) {
            for (var s = 0; s < sessions.sessions.length; s += 1) {
                var session = sessions.sessions[s];
                if (!session)
                    continue;
                var issuedAt = timeOf(session.issuedAt);
                if (issuedAt === null)
                    continue;
                entries.push({
                    at: issuedAt,
                    tone: "security",
                    icon: DEVICE_ICON,
                    title: t(hubEl, "i18nNotificationsNewSignIn", "New sign-in"),
                    detail: session.deviceDescription ||
                        t(hubEl, "i18nSecurityUnknownDevice", "Unrecognised device"),
                    panel: "security",
                });
            }
        }
        // Auth methods need no request — GET /me already carried them here.
        // A revoked one is not an event: it was removed, and this screen has
        // no record of when.
        for (var m = 0; m < profile.authMethods.length; m += 1) {
            var method = profile.authMethods[m];
            if (!method || method.revokedAt)
                continue;
            var linkedAt = timeOf(method.linkedAt);
            if (linkedAt === null)
                continue;
            entries.push({
                at: linkedAt,
                tone: "security",
                icon: LINK_ICON,
                title: t(hubEl, "i18nNotificationsMethodAdded", "Sign-in method connected"),
                detail: providerLabel(hubEl, method.type),
                panel: "connected",
            });
        }
        entries.sort(function (a, b) {
            return b.at - a.at;
        });
        return entries.slice(0, MAX_ROWS);
    }
    /** The provider's own name where the Hub has one, never a raw type string. */
    function providerLabel(hubEl, type) {
        var key = "i18nProvider" + type.charAt(0).toUpperCase() + type.slice(1);
        var named = hubEl.dataset[key];
        if (named)
            return named;
        if (type === "email")
            return t(hubEl, "i18nSettingsEmail", "Email");
        return type;
    }
    function rowHtml(hubEl, entry, locale) {
        var tone = TONE[entry.tone] || NEUTRAL_TONE;
        var open = entry.panel
            ? '<button type="button" data-flix-hub-nav="' + esc(entry.panel) + '" '
            : "<div ";
        return (open +
            'data-flix-notification style="width:100%;display:flex;align-items:flex-start;gap:13px;' +
            "padding:14px 16px;border-bottom:1px solid var(--border);background:none;border-inline:none;" +
            "border-top:none;text-align:start;font-family:inherit;" +
            (entry.panel ? "cursor:pointer;" : "") +
            'transition:background .2s"' +
            (entry.panel ? ' style-hover="background:var(--surface2)"' : "") +
            ">" +
            '<span aria-hidden="true" style="flex:none;width:34px;height:34px;border-radius:10px;background:' +
            tone.background +
            ";border:1px solid " +
            tone.border +
            ";color:" +
            tone.color +
            ';display:flex;align-items:center;justify-content:center">' +
            entry.icon +
            "</span>" +
            '<div style="flex:1;min-width:0">' +
            '<div style="font-size:14px;font-weight:700;color:var(--text);line-height:1.35">' +
            esc(entry.title) +
            "</div>" +
            (entry.detail
                ? '<div style="font-size:12px;color:var(--dim);margin-top:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' +
                    esc(entry.detail) +
                    "</div>"
                : "") +
            "</div>" +
            '<span style="flex:none;font-size:11px;color:var(--faint);padding-top:2px">' +
            esc(whenLabel(hubEl, entry.at, locale)) +
            "</span>" +
            (entry.panel ? "</button>" : "</div>"));
    }
    async function render(hubEl, contentEl, profile) {
        var locale = document.documentElement.lang || "en";
        contentEl.innerHTML = window.FlixHubRender.loadingState(t(hubEl, "i18nLoading", "Loading…"));
        var entries = await collect(hubEl, profile);
        if (entries.length === 0) {
            contentEl.innerHTML = window.FlixHubRender.emptyState({
                title: t(hubEl, "i18nNotificationsEmptyTitle", "Nothing yet"),
                message: t(hubEl, "i18nNotificationsEmptyMessage", "Deliveries, orders and sign-ins will show up here as they happen."),
                iconSvg: BELL_ICON,
            });
            window.flixCompileHoverStyles(contentEl);
            return;
        }
        contentEl.innerHTML =
            sectionLabelHtml(t(hubEl, "i18nNotificationsLabel", "Recent activity")) +
                '<div data-flix-notifications-list style="border-radius:var(--radius-md);background:var(--surface);' +
                'border:1px solid var(--border);overflow:hidden">' +
                entries
                    .map(function (entry) {
                    return rowHtml(hubEl, entry, locale);
                })
                    .join("") +
                "</div>" +
                '<p style="font-size:12px;color:var(--faint);margin:12px 2px 0;line-height:1.6">' +
                esc(t(hubEl, "i18nNotificationsHint", "This is a record of activity on your account. It is not stored, so nothing here can be marked as read.")) +
                "</p>";
        // The last row's divider would otherwise draw a line against the
        // card's own border.
        var rows = contentEl.querySelectorAll("[data-flix-notification]");
        var last = rows[rows.length - 1];
        if (last)
            last.style.borderBottom = "none";
        window.flixCompileHoverStyles(contentEl);
    }
    window.FlixHubNotifications = { render: render };
})();
